import { Injectable, Injector, inject } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { AppStorageService } from './app-storage.service';
import { EmbedService } from './embed.service';
import { LanguageService } from './language.service';
import { isMigratableKey, MIGRATION_HASH_PREFIX, MIGRATION_NOTICE_KEY, readMigrationPayload } from '../utils/migration';

@Injectable({
  providedIn: 'root'
})
export class MigrationService {
  private readonly storage = inject(AppStorageService);
  private readonly embed = inject(EmbedService).isEmbed();
  private readonly alertController = inject(AlertController);
  private readonly modalCtrl = inject(ModalController);
  // LanguageService reads storage in its constructor, so it may only be created after the import.
  private readonly injector = inject(Injector);
  // The handed-over offline resource ids; null = this load imported nothing.
  private importedOffline: string[] | null = null;

  // Runs as the first app initializer: every service that reads storage is created after it.
  importFromLocation(): void {
    const hash = window.location.hash;
    if (!hash.startsWith(MIGRATION_HASH_PREFIX)) return;
    // Always stripped, so a reload never imports again and the payload does not stay in the address bar.
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);

    const payload = readMigrationPayload(hash);
    // Only a fresh origin imports, so a handoff can never overwrite a choice made on the reader itself.
    if (!payload || this.embed || this.hasAppKeys()) return;
    Object.entries(payload.ls).forEach(([key, value]) => this.storage.setItem(key, value));
    this.importedOffline = payload.offline;
  }

  async showNoticeIfPending(): Promise<void> {
    if (this.importedOffline === null || this.storage.getItem(MIGRATION_NOTICE_KEY)) return;
    this.storage.setItem(MIGRATION_NOTICE_KEY, '1');

    const language = this.injector.get(LanguageService);
    const hasOffline = this.importedOffline.length > 0;
    const offlineButton = {
      text: language.translate('migration.openOffline'),
      cssClass: 'migration-notice-offline',
      handler: () => {
        void this.openOfflineManagement();
      }
    };
    const alert = await this.alertController.create({
      cssClass: 'migration-notice',
      header: language.translate('migration.title'),
      message:
        language.translate('migration.message') +
        (hasOffline ? ' ' + language.translate('migration.offlineMessage') : ''),
      buttons: [
        ...(hasOffline ? [offlineButton] : []),
        { text: language.translate('common.ok'), role: 'cancel', cssClass: 'migration-notice-ok' }
      ]
    });
    await alert.present();
  }

  private hasAppKeys(): boolean {
    return this.storage.keys().some((key) => key === MIGRATION_NOTICE_KEY || isMigratableKey(key));
  }

  private async openOfflineManagement(): Promise<void> {
    const { OfflineManagementComponent } = await import('../pages/offline-management/offline-management.component');
    const modal = await this.modalCtrl.create({
      component: OfflineManagementComponent
    });
    await modal.present();
  }
}
